import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Clock, Target, Pin, PinOff, Tag, PlusCircle, CheckSquare, ChevronDown,
  Calendar, CheckCircle2, AlertCircle, X, HelpCircle,
  Loader2 // <-- Import Loader2
} from 'lucide-react';
import {
  SEASON_CANONICALS, SUBJECT_ALIASES, GRADE_OPTIONS, TYPE_ALIASES
} from './ProductHubConstants';

import { doc, updateDoc, deleteField } from 'firebase/firestore';

// --- Helper Components ---
const Pill = ({ children, tone = 'slate', onClick = null, onRemove = null, className = '' }) => {
  const tones = {
    slate: 'bg-slate-100 text-slate-700', purple: 'bg-purple-100 text-purple-800',
    amber: 'bg-amber-100 text-amber-800', green: 'bg-green-100 text-green-800',
    red: 'bg-red-100 text-red-800', blue: 'bg-sky-100 text-sky-800'
  };
  const baseClasses = `inline-flex items-center text-xs font-semibold rounded-full ${tones[tone]} ${className}`;
  const contentClasses = onRemove ? "pl-2.5 pr-1.5 py-1" : "px-2.5 py-1";
  const content = (
    <>
      <span className={contentClasses}>{children}</span>
      {onRemove && (
        <button onClick={onRemove} className="ml-1 p-0.5 rounded-full hover:bg-black/10 flex-shrink-0" aria-label="Remove tag">
          <X size={12} />
        </button>
      )}
    </>
  );
  if (onClick) return <button onClick={onClick} className={`${baseClasses} hover:brightness-95 transition-all`}>{content}</button>;
  return <span className={baseClasses}>{content}</span>;
};

const InfoPopup = ({ text }) => (
  <div className="relative group flex items-center">
    <HelpCircle size={14} className="text-slate-400 cursor-help" />
    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-60 p-3 bg-slate-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-all z-10 pointer-events-none translate-y-2">
      {text}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-slate-800 rotate-180"></div>
    </div>
  </div>
);
// --- End Helper Components ---


const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'Never';
  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    if (isNaN(date.getTime())) return 'Processing...';
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ', ' +
           date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return 'Error';
  }
};

const addUnique = (arr = [], val) => {
  if (!val) return arr;
  const set = new Set(arr);
  set.add(val);
  return Array.from(set);
};
const removeVal = (arr = [], val) => (arr || []).filter(v => v !== val);

// --- TagSection & HeadlineTagEditors ---
const TagSection = ({ tone = 'slate', current = [], suggestions = [], allOptions = [], onAdd, onRemove }) => {
  return (
    <div>
      <div className="flex flex-wrap items-center gap-1.5 mb-2">
        {current.length ? current.map(val => <Pill key={`curr-${val}`} tone={tone} className="px-2 py-0.5" onRemove={() => onRemove?.(val)}>{val}</Pill>) : <span className="text-xs text-slate-400">None yet</span>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {suggestions.length > 0 ? ( suggestions.map(s => (
          <button key={`sugg-${s}`} className="text-xs font-semibold px-2 py-1 rounded-md border bg-white hover:bg-slate-50" onClick={() => onAdd?.(s)} title={`Add ${s}`}> + {s} </button>
        )) ) : ( <span className="text-xs text-slate-400">No suggestions found</span> )}
        <select className="text-xs border border-slate-300 rounded-md px-2 py-1 bg-white" value="" onChange={(e) => { const val = e.target.value; if (val) onAdd?.(val); e.target.value = ""; }}>
          <option value="" disabled>Choose…</option>
          {allOptions.map(opt => (<option key={`opt-${opt}`} value={opt}>{opt}</option>))}
        </select>
      </div>
    </div>
  );
};

const HeadlineTagEditors = ({
  product, onUpdateSeasonalTags, onUpdateSubjectTags, onUpdateGradeTags, onUpdateResourceTypes
}) => {
  const allSubjectOptions = useMemo(() => {
    const areas = new Set(SUBJECT_ALIASES.map(s => s.subjectArea));
    const specifics = new Set(SUBJECT_ALIASES.map(s => s.canonical));
    return [...Array.from(areas).sort(), ...Array.from(specifics).sort()];
  }, []);

  const subjectSuggestions = useMemo(() => {
    return Array.from(new Set([
      ...(product._subjectAreaTags || []),
      ...(product._specificSubjectTags || [])
    ]));
  }, [product._subjectAreaTags, product._specificSubjectTags]);

  return (
    <>
      <div className="flex items-center gap-2"> <Calendar size={16} className="text-slate-400" />
        <h4 className="text-sm font-bold uppercase tracking-wider text-slate-700">Seasonal Theme</h4> </div>
      <TagSection tone="purple" current={product.seasonalTags || []} suggestions={product._seasonalRefs || []} allOptions={SEASON_CANONICALS} onAdd={(val) => onUpdateSeasonalTags?.(product.id, addUnique(product.seasonalTags, val))} onRemove={(val) => onUpdateSeasonalTags?.(product.id, removeVal(product.seasonalTags, val))} />

      <div className="flex items-center gap-2"> <Tag size={16} className="text-slate-400" />
        <h4 className="text-sm font-bold uppercase tracking-wider text-slate-700">Subject</h4> </div>
      <TagSection tone="green" current={product.subjectTags || []} suggestions={subjectSuggestions} allOptions={allSubjectOptions} onAdd={(val) => onUpdateSubjectTags?.(product.id, addUnique(product.subjectTags, val))} onRemove={(val) => onUpdateSubjectTags?.(product.id, removeVal(product.subjectTags, val))} />

      <div className="flex items-center gap-2"> <Tag size={16} className="text-slate-400" />
        <h4 className="text-sm font-bold uppercase tracking-wider text-slate-700">Grade</h4> </div>
      <TagSection tone="blue" current={product.gradeTags || []} suggestions={product._gradeRefs || []} allOptions={GRADE_OPTIONS} onAdd={(val) => onUpdateGradeTags?.(product.id, addUnique(product.gradeTags, val))} onRemove={(val) => onUpdateGradeTags?.(product.id, removeVal(product.gradeTags, val))} />

      <div className="flex items-center gap-2"> <Tag size={16} className="text-slate-400" />
        <h4 className="text-sm font-bold uppercase tracking-wider text-slate-700">Resource Type</h4> </div>
      <TagSection tone="amber" current={product.resourceTypeTags || []} suggestions={product._typeRefs || []} allOptions={TYPE_ALIASES.map(t => t.canonical)} onAdd={(val) => onUpdateResourceTypes?.(product.id, addUnique(product.resourceTypeTags, val))} onRemove={(val) => onUpdateResourceTypes?.(product.id, removeVal(product.resourceTypeTags, val))} />
    </>
  );
};
// --- End TagSection & HeadlineTagEditors ---


const CoachPanel = ({
  product, onGoToSeo, onQuickFix, onUpdatePins,
  onUpdateSeasonalTags, onUpdateSubjectTags, onUpdateGradeTags, onUpdateResourceTypes,
  onUpdateCustomTags, onMarkProductUpdated, onMarkUploadedToTpt, onUpdateAuditOverrides,
  isDemoMode, triggerAuthModal,
  db,
  currentUserId
}) => {
  const [pinnedKeywords, setPinnedKeywords] = useState(product?.pinnedKeywords || []);
  const [customTags, setCustomTags] = useState(product?.customTags || []);
  const [newTagInput, setNewTagInput] = useState('');
  const [tagsExpanded, setTagsExpanded] = useState(false);

  // State to track which override keys are currently saving
  const [loadingOverrides, setLoadingOverrides] = useState({});

  useEffect(() => {
    setPinnedKeywords(product?.pinnedKeywords || []);
    setCustomTags(product?.customTags || []);
    setNewTagInput('');
    setLoadingOverrides({}); // Clear loading state when product changes
  }, [product?.id, product?.pinnedKeywords, product?.customTags]);


  const keywordsToShow = useMemo(() => {
    const pins = product?.pinnedKeywords || [];
    const all = product?.allKeywords || [];
    if (pins.length > 0) return all.slice().sort((a,b) => (pins.includes(b.keyword) - pins.includes(a.keyword)));
    else return all.slice().sort((a, b) => (a.curr ?? Infinity) - (b.curr ?? Infinity)).slice(0, 5);
  }, [product?.pinnedKeywords, product?.allKeywords]);

  const togglePin = (keywordPhrase) => {
    if (isDemoMode) return triggerAuthModal?.('Sign up to pin keywords!');
    let newPins;
    if (pinnedKeywords.includes(keywordPhrase)) {
      newPins = pinnedKeywords.filter(p => p !== keywordPhrase);
    } else if (pinnedKeywords.length < 5) {
      newPins = [...pinnedKeywords, keywordPhrase];
    } else {
      alert("You can pin a maximum of 5 keywords.");
      return;
    }
    setPinnedKeywords(newPins);
    onUpdatePins?.(product.id, newPins);
  };

  // Keep persistOverride separate for clarity
  const persistOverride = useCallback(async (key, checked) => {
     if (!db || !currentUserId || !product?.id) return;
    const ref = doc(db, 'userProducts', currentUserId, 'products', product.id);
    try {
        if (checked) {
          await updateDoc(ref, { [`auditOverrides.${key}`]: true });
        } else {
          await updateDoc(ref, { [`auditOverrides.${key}`]: deleteField() });
        }
    } catch (e) {
        console.error(`Error persisting override for key ${key}:`, e);
        throw e; // Re-throw error to be caught in handleOverrideToggle
    }
  }, [db, currentUserId, product?.id]); // Depend on product.id too

  // Final Revised handleOverrideToggle
  const handleOverrideToggle = useCallback(async (key, checked) => {
    // 1. Prevent double-clicks
    if (loadingOverrides[key]) return;

    // 2. Set loading state LOCALLY for this specific key
    setLoadingOverrides(prev => ({ ...prev, [key]: true }));

    // 3. Trigger optimistic update in PARENT component IMMEDIATELY
    //    This ensures the score recalculates and the 'checked' prop updates instantly.
    onUpdateAuditOverrides?.(product.id, { [key]: checked });

    // 4. Perform asynchronous save
    try {
      await persistOverride(key, checked);
      // If save succeeds, the optimistic state is now confirmed.
    } catch (e) {
      console.error('Failed to persist auditOverrides:', e);
      // OPTIONAL: If save fails, revert the parent's state
      // This will visually uncheck/recheck the box after the loading spinner disappears.
      // onUpdateAuditOverrides?.(product.id, { [key]: !checked });
      alert(`Failed to save setting for "${key}". The change might revert.`);
    } finally {
      // 5. Clear loading state for this key AFTER save attempt is complete
      setLoadingOverrides(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }, [product.id, onUpdateAuditOverrides, persistOverride, loadingOverrides]); // Dependencies are correct


  const handleAddTag = () => {
    if (isDemoMode) return triggerAuthModal?.('Sign up to add custom tags!');
    const tagToAdd = newTagInput.trim();
    if (!tagToAdd) return;
    if ((customTags || []).includes(tagToAdd)) {
      alert(`Tag "${tagToAdd}" already exists.`);
      return;
    }
    const updatedTags = [...(customTags || []), tagToAdd];
    setCustomTags(updatedTags);
    onUpdateCustomTags?.(product.id, updatedTags);
    setNewTagInput('');
  };
  const handleRemoveTag = (tagToRemove) => {
    if (isDemoMode) return triggerAuthModal?.();
    const updatedTags = (customTags || []).filter(tag => tag !== tagToRemove);
    setCustomTags(updatedTags);
    onUpdateCustomTags?.(product.id, updatedTags);
  };

  const { audit, _health, _insight, allKeywords = [] } = product;
  const lastAuditedDate = audit.lastAuditedAt;

  // Get overrides directly from the product prop (this is correct)
  const currentOverrides = product?.auditOverrides || {};

  const InsightCard = ({ insight }) => {
    const tones = { red: 'border-red-200 bg-red-50 text-red-800', amber: 'border-amber-200 bg-amber-50 text-amber-800', green: 'border-green-200 bg-green-50 text-green-800', purple: 'border-purple-200 bg-purple-50 text-purple-800', blue: 'border-sky-200 bg-sky-50 text-sky-800' };
    const Icon = insight.icon;
    return (
      <div className={`flex items-start gap-3 p-3 rounded-lg border ${tones[insight.tone]}`}>
        <Icon size={20} className="shrink-0 mt-0.5" />
        <p className="text-sm" dangerouslySetInnerHTML={{ __html: insight.text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
      </div>
    );
  };

  return (
    <div className="bg-white p-6 space-y-6">
      <div className="border-b border-slate-100 pb-4">
        <h3 className="text-lg font-bold tracking-tight text-slate-800 line-clamp-2">{product.productTitle}</h3>
        <div className="mt-2 flex items-center justify-between">
          <div className="flex items-center gap-2"> <span className="font-semibold text-sm">Health:</span> <Pill tone={_health.tone}>{_health.label}</Pill> </div>
          <div className="text-xs text-slate-400 flex items-center gap-1"> <Clock size={12} /> <span>Audited: {formatTimestamp(lastAuditedDate)}</span> </div>
        </div>
      </div>

      {_insight && <InsightCard insight={_insight} />}


      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
        <div>
          <div className="flex items-center justify-between mb-3 border-b border-slate-100 pb-2">
            <div className="flex items-center gap-1.5">
              <h4 className="text-sm font-bold uppercase tracking-wider text-slate-700">Action Plan</h4>
              <InfoPopup text="This is your product's SEO and quality score. Check 'Not Relevant' on any items that don't apply to this product to see your score update." />
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <span>Score:</span>
              <Pill tone="blue" className="px-2 py-0.5">{audit.score}</Pill>
            </div>
          </div>
          <div className="space-y-4">
            {(audit.items || []).map(item => {
              // Read checked state directly from the product prop coming from the parent
              const isNR = !!currentOverrides[item.key];
              const pass = item.pass;
              // Check loading state locally
              const isLoading = loadingOverrides[item.key];

              return (
                <div
                  key={item.key}
                  className={`rounded-lg border transition-colors ${pass ? 'bg-white border-slate-200' : 'bg-amber-50/60 border-amber-200'} ${isNR ? 'opacity-70' : ''}`}
                >
                  <div className="flex items-start gap-3 p-3">
                    {pass ? ( <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 shrink-0" /> ) : ( <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" /> )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-800 text-sm">{item.label}</div>
                      <div className={`text-xs mt-1 ${!pass ? 'text-amber-800' : 'text-slate-500'}`}>
                        {item.hint}
                      </div>
                      {!pass && !isNR && (
                        <button
                          onClick={() => onQuickFix(item.key, product)}
                          className="mt-1 text-xs font-semibold text-sky-600 hover:underline"
                          title="Fix now in Description Rewriter"
                        >
                          Fix now &rarr;
                        </button>
                      )}
                      {!pass && isNR && (
                        <div className="text-xs text-slate-500 mt-0.5 italic">
                          (Override: Not Relevant)
                        </div>
                      )}
                    </div>

                    {/* Label uses local loading state, checkbox uses parent's checked state */}
                    <label className={`flex items-center gap-1 text-xs font-semibold text-slate-600 shrink-0 ${isLoading ? 'cursor-wait opacity-70' : 'cursor-pointer'}`}>
                      <input
                        type="checkbox"
                        className="rounded border-slate-300 disabled:opacity-50 disabled:cursor-wait"
                        checked={isNR} // Use parent state for visual consistency
                        onChange={(e) => handleOverrideToggle(item.key, e.target.checked)}
                        disabled={isLoading} // Disable input while loading
                      />
                      {isLoading ? (
                        <Loader2 size={12} className="animate-spin text-slate-400 mx-1" />
                      ) : (
                        'Not Relevant'
                      )}
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* --- Tracked Keywords, Tags, Updates sections --- */}
         <div className="space-y-6">
          <div className="pt-2">
            <div className="flex items-center gap-1.5 mb-2">
              <Target size={16} className="text-slate-400" />
              <h4 className="text-sm font-bold uppercase tracking-wider text-slate-700">Tracked Keywords</h4>
              <InfoPopup text="Keywords you are tracking in the SEO & Rank Tracker tool. Pin your top 5 to keep them visible here. Click a keyword to go to the tracker." />
            </div>
            {(allKeywords || []).length === 0 ? (
              <div className="text-center p-4 rounded-lg bg-slate-50 border">
                <p className="font-semibold text-slate-700 text-sm">No Keywords Tracked</p>
                <p className="text-xs text-slate-500 mb-3">Track keywords to see rank and performance here.</p>
                <button onClick={() => onGoToSeo?.(product)} className="text-sm font-semibold text-sky-600 hover:text-sky-800"> Add Keywords &rarr; </button>
              </div> ) : (
              <div>
                <p className="text-xs text-slate-500 mb-2"> {(product?.pinnedKeywords || []).length ? "Pin your top 5 keywords." : "Showing top 5 keywords. Click pin icon to save."} </p>
                <div className="space-y-1 text-sm max-h-48 overflow-y-auto pr-1">
                  {(product?.allKeywords || []).slice().sort((a,b) => ((product?.pinnedKeywords || []).includes(b.keyword) - (product?.pinnedKeywords || []).includes(a.keyword)) || ((a.curr ?? Infinity) - (b.curr ?? Infinity))).slice(0, 5).map(kw => {
                    const isPinned = pinnedKeywords.includes(kw.keyword);
                    const delta = kw?.delta ?? null;
                    return (
                      <div key={kw.keyword} className={`flex items-center justify-between p-2 rounded-md group ${isPinned ? 'bg-sky-50' : 'hover:bg-slate-50'}`}>
                        <div className="flex items-center gap-2 overflow-hidden mr-2">
                          <span className="font-medium text-slate-700 truncate">{kw.keyword}</span> <span className="text-xs text-slate-500 shrink-0">#{kw.curr ?? 'N/A'}</span>
                          <span className={`text-xs ${delta > 0 ? 'text-green-600' : delta < 0 ? 'text-red-600' : 'text-slate-400'}`}> {delta > 0 ? '↑' : delta < 0 ? '↓' : '•'} {Math.abs(delta)} </span>
                        </div>
                        <button onClick={() => togglePin(kw.keyword)} title={isPinned ? "Unpin Keyword" : "Pin Keyword"} disabled={!isPinned && pinnedKeywords.length >= 5} className="disabled:opacity-20 shrink-0">
                          {isPinned ? <PinOff size={14} className="text-slate-400 hover:text-red-500" /> : <Pin size={14} className="text-slate-400 hover:text-sky-500 opacity-20 group-hover:opacity-100 transition-opacity" />}
                        </button>
                      </div>
                    );
                  })}
                </div>
                <button onClick={() => onGoToSeo?.(product)} className="mt-3 text-sm font-semibold text-sky-600 hover:text-sky-800 w-full text-left"> Open SEO Tracker &rarr; </button>
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-slate-200">
            <button type="button" className="flex items-center justify-between w-full list-none hover:bg-slate-50 -mx-2 px-2 py-1 rounded cursor-pointer" onClick={() => setTagsExpanded(!tagsExpanded)}>
              <div className="flex items-center gap-1.5"> <Tag size={16} className="text-slate-400" />
                <h4 className="text-sm font-bold uppercase tracking-wider text-slate-700">Headline Tags</h4> <InfoPopup text="Add or correct key tags like Grade, Subject, Season, and Resource Type if they weren't automatically detected." /> </div>
              <ChevronDown size={18} className={`text-slate-400 transition-transform ${tagsExpanded ? 'rotate-180' : ''}`} />
            </button>
            {tagsExpanded && ( <div className="mt-3 space-y-4 pl-1"> <HeadlineTagEditors product={product} onUpdateSeasonalTags={onUpdateSeasonalTags} onUpdateSubjectTags={onUpdateSubjectTags} onUpdateGradeTags={onUpdateGradeTags} onUpdateResourceTypes={onUpdateResourceTypes} /> </div> )}
          </div>

          <div className="pt-4 border-t border-slate-200">
            <div className="flex items-center gap-1.5 mb-2"> <Tag size={16} className="text-slate-400" />
              <h4 className="text-sm font-bold uppercase tracking-wider text-slate-700">Custom Tags</h4> <InfoPopup text="Add your own private tags to organize your products (e.g., 'To-Do', 'Needs Covers', 'Q4 Marketing')." /> </div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {(customTags || []).length > 0 ? customTags.map(tag => ( <Pill key={tag} tone="slate" onRemove={() => handleRemoveTag(tag)} className="text-slate-800"> {tag} </Pill> )) : ( <p className="text-xs text-slate-400">No custom tags added yet.</p> )}
            </div>
            <div className="flex items-center gap-2">
              <input type="text" value={newTagInput} onChange={(e) => setNewTagInput(e.target.value)} placeholder="Add a tag..." className="flex-grow px-2 py-1 border border-slate-300 rounded-md text-sm focus:ring-1 focus:ring-sky-500 focus:border-sky-500" disabled={isDemoMode} onKeyDown={(e) => { if (e.key === 'Enter') handleAddTag(); }} />
              <button onClick={handleAddTag} disabled={isDemoMode || !newTagInput.trim()} className="p-1.5 bg-sky-600 hover:bg-sky-700 text-white rounded-md disabled:bg-slate-400 disabled:cursor-not-allowed" aria-label="Add Tag"> <PlusCircle size={16} /> </button>
            </div>
          </div>

          <div className="pt-4 border-t border-slate-200 space-y-3">
            <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md p-2">
              <div className="flex items-center gap-2 text-slate-600 text-sm"> <CheckSquare size={16} /> <span className="cursor-help" title="Click 'Yes' if you've recently improved the title or description. This resets the 'Needs Refresh' tag."> Made recent updates? </span> </div>
              <button onClick={() => { if (onMarkProductUpdated) onMarkProductUpdated(product.id); else console.warn('onMarkProductUpdated is not provided'); }} className="text-xs font-semibold px-2 py-1 rounded-md border bg-white hover:bg-slate-50" disabled={isDemoMode}> Yes, Mark Updated </button>
            </div>
            <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md p-2">
              <div className="flex items-center gap-2 text-slate-600 text-sm"> <CheckSquare size={16} /> <span className="cursor-help" title="Click 'Yes' after you've saved your new changes on the TPT website."> Uploaded changes to TPT? </span> </div>
              <button onClick={() => { if (onMarkUploadedToTpt) onMarkUploadedToTpt(product.id); else console.warn('onMarkUploadedToTpt is not provided'); }} className="text-xs font-semibold px-2 py-1 rounded-md border bg-white hover:bg-slate-50" disabled={isDemoMode}> Yes, Uploaded </button>
            </div>
          </div>
        </div>
        {/* --- End Sections --- */}

      </div>
    </div>
  );
};

export default CoachPanel;